import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import type {
  LmIoServerSetup,
  VmixOutput,
  LoadProgress,
  LoadResult,
  StudioConfigData,
  StudioConfigKind,
} from "@production-hub/shared";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type StudioConfig = {
  id: string;
  studio_id: string;
  project_id: string | null;
  project_name: string | null;
  studio_name: string | null;
  kind: StudioConfigKind;
  is_default: boolean;
  source_config_id: string | null;
  source_config_name: string | null;
  source_version: number | null;
  kept: boolean;
  last_loaded_at: string | null;
  name: string;
  description: string | null;
  version: number;
  config_data: StudioConfigData;
  created_at: string;
  updated_at: string;
};

export type StudioConfigVersion = {
  id: string;
  config_id: string;
  version: number;
  config_data: StudioConfigData;
  source: "cloud" | "bridge";
  base_version: number | null;
  conflict_base_version: number | null;
  conflict_resolved_at: string | null;
  loaded_at: string | null;
  created_at: string;
};

export type StudioConfigLoad = {
  id: string;
  studio_id: string;
  config_id: string | null;
  config_name: string | null;
  requested_config_id: string | null;
  requested_config_name: string | null;
  version: number;
  mode: "full" | "setup";
  as_copy: boolean;
  status: "pending" | "running" | "done" | "failed" | "superseded" | "expired";
  result: LoadResult | null;
  /** Progress of a running load (slow steps such as media) */
  progress: LoadProgress | null;
  requested_by_name: string | null;
  created_at: string;
  finished_at: string | null;
};

type ZeroStudioConfig = {
  id: string;
  studioID: string;
  projectID?: string | null;
  kind?: StudioConfigKind;
  isDefault?: boolean;
  sourceConfigID?: string | null;
  sourceVersion?: number | null;
  kept?: boolean;
  lastLoadedAt?: number | null;
  name: string;
  description?: string | null;
  version: number;
  configData?: StudioConfigData;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  project?: { name?: string | null } | null;
  studio?: { name?: string | null } | null;
  sourceConfig?: { name?: string | null } | null;
};

type ZeroStudioConfigVersion = {
  id: string;
  configID: string;
  version: number;
  configData?: StudioConfigData;
  source?: "cloud" | "bridge";
  baseVersion?: number | null;
  conflictBaseVersion?: number | null;
  conflictResolvedAt?: number | null;
  loadedAt?: number | null;
  createdAt?: number | string | null;
};

type ZeroStudioConfigLoad = {
  id: string;
  studioID: string;
  configID?: string | null;
  requestedConfigID?: string | null;
  version: number;
  mode: "full" | "setup";
  asCopy: boolean;
  status: StudioConfigLoad["status"];
  result?: LoadResult | null;
  progress?: LoadProgress | null;
  createdAt?: number | null;
  finishedAt?: number | null;
  config?: { name?: string | null } | null;
  requestedConfig?: { name?: string | null } | null;
  requestedByPerson?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null;
};

const emptyConfigData: StudioConfigData = {
  layouts: [],
};

const optionalTimestamp = (value: number | string | null | undefined) =>
  value ? toLegacyTimestamp(value) : null;

export function toLegacyConfig(row: ZeroStudioConfig): StudioConfig {
  return {
    id: row.id,
    studio_id: row.studioID,
    project_id: row.projectID ?? null,
    project_name: row.project?.name ?? null,
    studio_name: row.studio?.name ?? null,
    kind: row.kind ?? "config",
    is_default: row.isDefault ?? false,
    source_config_id: row.sourceConfigID ?? null,
    source_config_name: row.sourceConfig?.name ?? null,
    source_version: row.sourceVersion ?? null,
    kept: row.kept ?? false,
    last_loaded_at: optionalTimestamp(row.lastLoadedAt),
    name: row.name,
    description: row.description ?? null,
    version: row.version,
    config_data: row.configData ?? emptyConfigData,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyVersion(row: ZeroStudioConfigVersion): StudioConfigVersion {
  return {
    id: row.id,
    config_id: row.configID,
    version: row.version,
    config_data: row.configData ?? emptyConfigData,
    source: row.source ?? "cloud",
    base_version: row.baseVersion ?? null,
    conflict_base_version: row.conflictBaseVersion ?? null,
    conflict_resolved_at: optionalTimestamp(row.conflictResolvedAt),
    loaded_at: optionalTimestamp(row.loadedAt),
    created_at: toLegacyTimestamp(row.createdAt),
  };
}

function toLegacyLoad(row: ZeroStudioConfigLoad): StudioConfigLoad {
  const person = row.requestedByPerson;
  const personName = person
    ? [person.firstName, person.lastName].filter(Boolean).join(" ") || null
    : null;
  return {
    id: row.id,
    studio_id: row.studioID,
    config_id: row.configID ?? null,
    config_name: row.config?.name ?? null,
    requested_config_id: row.requestedConfigID ?? null,
    requested_config_name: row.requestedConfig?.name ?? null,
    version: row.version,
    mode: row.mode,
    as_copy: row.asCopy,
    status: row.status,
    result: row.result ?? null,
    progress: row.progress ?? null,
    requested_by_name: personName,
    created_at: toLegacyTimestamp(row.createdAt),
    finished_at: optionalTimestamp(row.finishedAt),
  };
}

function queryState(details: { type: string; error?: unknown }) {
  return {
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error as never))
        : null,
  };
}

export const useStudioConfigs = (studioId: string | undefined) => {
  const query = studioId
    ? appQueries.studioConfigs.byStudio({ studioID: studioId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!studioId });
  const configs = ((data ?? []) as ZeroStudioConfig[]).map(toLegacyConfig);
  return {
    data: configs,
    isLoading:
      !!studioId && details.type !== "complete" && configs.length === 0,
    ...queryState(details),
  };
};

export const useProjectStudioConfigs = (projectId: string | undefined) => {
  const query = projectId
    ? appQueries.studioConfigs.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const configs = ((data ?? []) as ZeroStudioConfig[]).map(toLegacyConfig);
  return {
    data: configs,
    isLoading:
      !!projectId && details.type !== "complete" && configs.length === 0,
    ...queryState(details),
  };
};

export const useSingleStudioConfig = (configId: string | undefined) => {
  const query = configId
    ? appQueries.studioConfigs.byID({ id: configId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!configId });
  return {
    data: data ? toLegacyConfig(data as ZeroStudioConfig) : null,
    isLoading: !!configId && details.type !== "complete" && !data,
    ...queryState(details),
  };
};

export const useConfigVersions = (configId: string | undefined) => {
  const query = configId
    ? appQueries.studioConfigs.versions({ configID: configId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!configId });
  const versions = ((data ?? []) as ZeroStudioConfigVersion[]).map(
    toLegacyVersion,
  );
  return {
    data: versions,
    isLoading:
      !!configId && details.type !== "complete" && versions.length === 0,
    ...queryState(details),
  };
};

export const useStudioConfigLoads = (
  studioId: string | undefined,
  limit?: number,
) => {
  const query = studioId
    ? appQueries.studioConfigLoads.byStudio({
        studioID: studioId,
        ...(limit ? { limit } : {}),
      })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!studioId });
  const loads = ((data ?? []) as ZeroStudioConfigLoad[]).map(toLegacyLoad);
  return {
    data: loads,
    isLoading: !!studioId && details.type !== "complete" && loads.length === 0,
    ...queryState(details),
  };
};

export const useStudioConfigLoad = (loadId: string | null | undefined) => {
  const query = loadId
    ? appQueries.studioConfigLoads.byID({ id: loadId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!loadId });
  return {
    data: data ? toLegacyLoad(data as ZeroStudioConfigLoad) : null,
    ...queryState(details),
  };
};

// Zero mutator payloads are typed as plain JSON. StudioConfigData is JSON-shaped
// at runtime but, as an interface, lacks the index signature TypeScript wants.
const asMutatorInput = <T,>(input: T) =>
  input as unknown as { [key: string]: never };

type MutatorName =
  | "copy"
  | "update"
  | "delete"
  | "restoreVersion"
  | "setDefaultTemplate"
  | "keep"
  | "saveToOriginal"
  | "resolveConflict"
  | "forceUnlock"
  | "setLmIoServer"
  | "setVmixOutput";

/**
 * One studioConfigs mutator as a hook: `mutateAsync` throws (toasting the
 * error), `mutate` toasts success/failure itself.
 */
function useStudioConfigMutator<TParams, TResult = void>(
  name: MutatorName,
  toInput: (params: TParams) => Record<string, unknown>,
  messages: { success?: string; error: string },
  result?: (input: Record<string, unknown>) => TResult,
) {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (params: TParams) =>
    runPending(async () => {
      const input = toInput(params);
      const mutators = appMutators.studioConfigs as unknown as Record<
        string,
        (input: unknown) => unknown
      >;
      const mutation = zero.mutate(mutators[name](asMutatorInput(input)));
      await mutation.client;
      await reportServerMutationFailure(mutation.server, messages.error);
      return (result ? result(input) : undefined) as TResult;
    });
  return {
    mutate: (
      params: TParams,
      options?: { onSuccess?: (r: TResult) => void },
    ) => {
      void run(params)
        .then((value) => {
          if (messages.success) toast.success(messages.success);
          options?.onSuccess?.(value);
        })
        .catch((err) => toast.error(`${messages.error}: ${err.message}`));
    },
    mutateAsync: async (params: TParams) => {
      try {
        const value = await run(params);
        if (messages.success) toast.success(messages.success);
        return value;
      } catch (err) {
        toast.error(
          `${messages.error}: ${err instanceof Error ? err.message : String(err)}`,
        );
        throw err;
      }
    },
    isPending,
  };
}

export type CopyStudioConfigParams = {
  source_config_id: string;
  source_version?: number;
  studio_id: string;
  project_id?: string | null;
  kind: "config" | "template";
  name: string;
  description?: string | null;
};

/** New configs and templates are copies (there is no empty config). */
export const useCopyStudioConfig = () =>
  useStudioConfigMutator<CopyStudioConfigParams, { id: string }>(
    "copy",
    (params) => ({
      id: generateUUID(),
      sourceConfigID: params.source_config_id,
      ...(params.source_version
        ? { sourceVersion: params.source_version }
        : {}),
      studioID: params.studio_id,
      projectID: params.kind === "config" ? (params.project_id ?? null) : null,
      kind: params.kind,
      name: params.name,
      description: params.description ?? null,
    }),
    { success: "Configuration copied", error: "Failed to copy config" },
    (input) => ({ id: input.id as string }),
  );

export const useUpdateStudioConfig = () => {
  const mutator = useStudioConfigMutator<{
    id: string;
    studio_id: string;
    name?: string;
    description?: string | null;
    project_id?: string | null;
    config_data?: StudioConfigData;
  }>(
    "update",
    (params) => ({
      id: params.id,
      studioID: params.studio_id,
      ...(params.name !== undefined ? { name: params.name } : {}),
      ...(params.description !== undefined
        ? { description: params.description }
        : {}),
      ...(params.project_id !== undefined
        ? { projectID: params.project_id }
        : {}),
      ...(params.config_data !== undefined
        ? { configData: params.config_data }
        : {}),
    }),
    { error: "Failed to update config" },
  );
  return mutator;
};

/** The config's lm-io-server setup (cloud-owned: also on the active config). */
export const useSetConfigLmIoServer = () =>
  useStudioConfigMutator<{
    id: string;
    studio_id: string;
    lm_io_server: LmIoServerSetup;
  }>(
    "setLmIoServer",
    (params) => ({
      id: params.id,
      studioID: params.studio_id,
      lmIoServer: params.lm_io_server,
    }),
    { error: "Failed to save the lm-io-server setup" },
  );

/** Which vMix output a DeckLink port shows (cloud-owned, signal tracing). */
export const useSetConfigVmixOutput = () =>
  useStudioConfigMutator<{
    id: string;
    studio_id: string;
    port_id: string;
    output: VmixOutput | null;
  }>(
    "setVmixOutput",
    (params) => ({
      id: params.id,
      studioID: params.studio_id,
      portID: params.port_id,
      output: params.output,
    }),
    { error: "Failed to save the vMix output" },
  );

export const useDeleteStudioConfig = () =>
  useStudioConfigMutator<{ id: string; studio_id: string }>(
    "delete",
    (params) => ({ id: params.id, studioID: params.studio_id }),
    { success: "Configuration deleted", error: "Failed to delete config" },
  );

export const useRestoreConfigVersion = () =>
  useStudioConfigMutator<{
    configId: string;
    versionId: string;
    studioId: string;
  }>(
    "restoreVersion",
    (params) => ({
      configID: params.configId,
      versionID: params.versionId,
      studioID: params.studioId,
    }),
    { success: "Version restored", error: "Failed to restore" },
  );

export const useSetDefaultTemplate = () =>
  useStudioConfigMutator<{ id: string; studio_id: string }>(
    "setDefaultTemplate",
    (params) => ({ id: params.id, studioID: params.studio_id }),
    { success: "Default template set", error: "Failed to set the default" },
  );

export const useKeepScratchConfig = () =>
  useStudioConfigMutator<{
    id: string;
    studio_id: string;
    kind: "config" | "template";
    name: string;
    project_id?: string | null;
  }>(
    "keep",
    (params) => ({
      id: params.id,
      studioID: params.studio_id,
      kind: params.kind,
      name: params.name,
      projectID: params.kind === "config" ? (params.project_id ?? null) : null,
    }),
    { success: "Scratch copy kept", error: "Failed to keep the copy" },
  );

export const useSaveScratchToOriginal = () =>
  useStudioConfigMutator<{ id: string; studio_id: string }>(
    "saveToOriginal",
    (params) => ({ id: params.id, studioID: params.studio_id }),
    { success: "Saved to the original", error: "Failed to save" },
  );

export const useResolveConfigConflict = () =>
  useStudioConfigMutator<{
    config_id: string;
    version_id: string;
    studio_id: string;
    keep: "conflict" | "current";
  }>(
    "resolveConflict",
    (params) => ({
      configID: params.config_id,
      versionID: params.version_id,
      studioID: params.studio_id,
      keep: params.keep,
    }),
    { success: "Conflict resolved", error: "Failed to resolve the conflict" },
  );

export const useForceUnlockConfig = () =>
  useStudioConfigMutator<{ studio_id: string }>(
    "forceUnlock",
    (params) => ({ studioID: params.studio_id }),
    { success: "Config unlocked", error: "Failed to unlock" },
  );
